"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Compass, PhoneCall } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Results() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="see-yourself-in-these-results" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground overflow-hidden">
      {/* Background Architectural Accent lines */}
      <div className="pointer-events-none absolute inset-0 flex justify-center">
        <div className="w-full max-w-7xl h-full border-x border-border/50" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={containerVariants} className="relative mx-auto max-w-4xl rounded-2xl border border-border bg-card/85 p-8 sm:p-12 lg:p-16 shadow-xl backdrop-blur-md">
          {/* Top Architectural Copper Accent Bar */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-24 sm:w-32 bg-primary rounded-b" />

          <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
            {/* Eyebrow badge */}
            <div className="mb-4">
              <Badge variant="outline" className="border-border bg-secondary/80 px-3.5 py-1 text-xs font-semibold tracking-wider uppercase text-foreground">
                Start Your Project
              </Badge>
            </div>

            {/* Main Section Heading */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
              See yourself in these results
            </h2>

            {/* Supporting Copy */}
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed text-balance">
              Tell us about your home in Decatur, and let us build a detailed plan and clear estimate for your next remodel.
            </p>

            {/* Call to Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-lg bg-primary text-primary-foreground px-7 py-3.5 font-medium shadow-md transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Request an estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-lg border-border bg-background/90 text-foreground px-6 py-3.5 font-medium shadow-xs transition-[background-color,border-color] duration-200 hover:bg-secondary">
                <Link to="/ProcessPlanningPage" className="inline-flex items-center justify-center gap-2">
                  <Compass className="size-4 text-muted-foreground" />
                  <span>Explore planning guide</span>
                </Link>
              </Button>
            </div>

            {/* Quick Contact Micro-Row */}
            <div className="mt-10 pt-6 border-t border-border/80 w-full flex flex-col sm:flex-row items-center justify-center gap-4 text-xs sm:text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="inline-block size-2 rounded-full bg-primary" />
                <span>Serving Oakhurst, Winnona Park & all Decatur neighborhoods</span>
              </div>
              <span className="hidden sm:inline text-border">•</span>
              <div className="flex items-center gap-1.5">
                <PhoneCall className="size-3.5 text-muted-foreground" />
                <span>Questions? Call (470) 847-9076</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}